import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn } from '@angular/router';
import { AuthFlowService } from '@core/services/auth-flow.service';
import { extractAttribution, rememberAttribution } from '@core/utils/acquisition-attribution';
import { extractPromoCode, rememberPromoCode } from '@core/utils/pending-promo-code';
import { extractPartnerCode, rememberPartnerCode } from '@core/utils/pending-partner-code';

/**
 * CaptureReturnUrlGuard
 *
 * Captures `returnUrl` from query params and hands it to
 * `AuthFlowService.rememberRedirectUrl()` so the post-auth flow
 * (WaitPageComponent and OAuth directives) can redirect users back to the
 * originally requested in-app route.
 *
 * A fresh `returnUrl` always replaces whatever an earlier navigation stored:
 * it is the most recent intent, and keeping the older value is what sent
 * refreshed users back to the first dashboard page of the session (#730).
 * Validation (internal path only, no open redirect) lives in AuthFlowService.
 *
 * It also captures acquisition attribution (UTMs, ad click IDs) forwarded by
 * the marketing site, so the signup it leads to can be credited (#870), and a
 * promotion code (`coupon` or `promo`) so checkout can apply it (#943), and a
 * channel-partner code (`partner`) for the API to check at company creation.
 *
 * This is intentionally a no-op during SSR.
 */
export const captureReturnUrlGuard: CanActivateFn = (route, state) => {
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId) || typeof sessionStorage === 'undefined') {
    return true;
  }

  const attribution = extractAttribution((name) => route.queryParamMap.get(name), state.url);
  if (attribution) {
    rememberAttribution(attribution);
  }

  const promoCode = extractPromoCode((name) => route.queryParamMap.get(name));
  if (promoCode) {
    rememberPromoCode(promoCode);
  }

  const partnerCode = extractPartnerCode((name) => route.queryParamMap.get(name));
  if (partnerCode) {
    rememberPartnerCode(partnerCode);
  }

  const returnUrl = route.queryParamMap.get('returnUrl');
  if (!returnUrl) {
    return true;
  }

  inject(AuthFlowService).rememberRedirectUrl(returnUrl);
  return true;
};
